import React, { useEffect, useMemo, useState } from 'react';

type TabId = 'teachers' | 'students';

type TeacherRow = {
  id: string;
  teacher: string;
  subject: string;
  klass: string;
  avg: number;
  delta: number;
  rating: 'excellent' | 'good' | 'average' | 'needs_support';
  students: number;
  review?: boolean;
  trend: number[];
};

type SubjectTrend = {
  id: string;
  name: string;
  points: number[];
  status: 'improving' | 'strong' | 'steady' | 'needs_attention';
  classHighest: number;
  latest: number;
};

const TABS: Array<{ id: TabId; label: string; hint: string; lead: string }> = [
  {
    id: 'teachers',
    label: 'Teacher analysis',
    hint: 'Class outcomes',
    lead: 'See which classes are thriving and where support may help — illustrative sample from exam results.',
  },
  {
    id: 'students',
    label: 'Student growth',
    hint: 'Parent-ready story',
    lead: 'Parents and students get clear progress stories and subject insights they can act on.',
  },
];

const TEACHERS: TeacherRow[] = [
  {
    id: 't1',
    teacher: 'Ms. Mehta',
    subject: 'Mathematics',
    klass: '10-A',
    avg: 88,
    delta: 4.2,
    rating: 'excellent',
    students: 42,
    trend: [76, 79, 82, 88],
  },
  {
    id: 't2',
    teacher: 'Mr. Khan',
    subject: 'Science',
    klass: '9-B',
    avg: 81,
    delta: 2.1,
    rating: 'excellent',
    students: 38,
    trend: [74, 77, 79, 81],
  },
  {
    id: 't3',
    teacher: 'Ms. Iyer',
    subject: 'English',
    klass: '8-C',
    avg: 72,
    delta: -1.4,
    rating: 'good',
    students: 40,
    trend: [75, 74, 73, 72],
  },
  {
    id: 't4',
    teacher: 'Mr. Das',
    subject: 'Mathematics',
    klass: '8-C',
    avg: 58,
    delta: -6.0,
    rating: 'average',
    students: 36,
    trend: [68, 64, 61, 58],
  },
  {
    id: 't5',
    teacher: 'Ms. Bose',
    subject: 'Social Science',
    klass: '7-A',
    avg: 93,
    delta: 8.5,
    rating: 'excellent',
    students: 34,
    review: true,
    trend: [78, 82, 87, 93],
  },
];

const SUBJECTS: SubjectTrend[] = [
  {
    id: 'math',
    name: 'Mathematics',
    points: [62, 68, 74, 81],
    status: 'improving',
    classHighest: 94,
    latest: 81,
  },
  {
    id: 'sci',
    name: 'Science',
    points: [78, 80, 79, 84],
    status: 'strong',
    classHighest: 91,
    latest: 84,
  },
  {
    id: 'eng',
    name: 'English',
    points: [71, 70, 72, 73],
    status: 'steady',
    classHighest: 89,
    latest: 73,
  },
  {
    id: 'sst',
    name: 'Social Science',
    points: [76, 71, 66, 63],
    status: 'needs_attention',
    classHighest: 92,
    latest: 63,
  },
];

const STUDENT_INSIGHTS = [
  {
    tone: 'positive' as const,
    title: 'Maths climbing fast',
    detail: '+19 pts across four exams — keep the weekly practice habit.',
  },
  {
    tone: 'watch' as const,
    title: 'Social Science needs a push',
    detail: 'Down 13 pts since Unit 1. A short revision set before PTM will help.',
  },
  {
    tone: 'positive' as const,
    title: 'Science top-quartile',
    detail: 'Only 7 pts from class highest — strong exam readiness.',
  },
];

const RATING_LABEL: Record<TeacherRow['rating'], string> = {
  excellent: 'Teaching well',
  good: 'Solid',
  average: 'Average',
  needs_support: 'Needs support',
};

function ratingClass(rating: TeacherRow['rating']): string {
  return `analytics-demo__rating analytics-demo__rating--${rating}`;
}

function statusClass(status: SubjectTrend['status']): string {
  return `analytics-demo__status analytics-demo__status--${status}`;
}

function statusLabel(status: SubjectTrend['status']): string {
  if (status === 'improving') return 'Improving';
  if (status === 'strong') return 'Strong';
  if (status === 'needs_attention') return 'Needs attention';
  return 'Steady';
}

function Sparkline({ points, tone = 'navy' }: { points: number[]; tone?: 'navy' | 'gold' | 'alert' }) {
  const w = 120;
  const h = 36;
  const min = Math.min(...points) - 4;
  const max = Math.max(...points) + 4;
  const span = Math.max(1, max - min);
  const coords = points
    .map((p, i) => {
      const x = (i / Math.max(1, points.length - 1)) * w;
      const y = h - ((p - min) / span) * (h - 4) - 2;
      return `${x},${y}`;
    })
    .join(' ');
  const last = points[points.length - 1];
  const lastX = w;
  const lastY = h - ((last - min) / span) * (h - 4) - 2;
  const area = `0,${h} ${coords} ${w},${h}`;

  return (
    <svg className={`analytics-demo__spark analytics-demo__spark--${tone}`} viewBox={`0 0 ${w} ${h}`} aria-hidden>
      <polygon className="analytics-demo__spark-area" points={area} />
      <polyline fill="none" strokeWidth="2" points={coords} />
      <circle cx={lastX} cy={lastY} r="3.2" />
    </svg>
  );
}

function GrowthBars({ points }: { points: number[] }) {
  const labels = ['UT1', 'UT2', 'Mid', 'Final'];
  const max = Math.max(...points, 1);
  return (
    <div className="analytics-demo__growth-bars" aria-hidden>
      {points.map((p, i) => (
        <div key={labels[i]} className="analytics-demo__growth-col">
          <div className="analytics-demo__growth-track">
            <div
              className="analytics-demo__growth-fill"
              style={{
                height: `${Math.max(10, Math.min(100, (p / max) * 100))}%`,
                animationDelay: `${0.08 + i * 0.07}s`,
              }}
            />
          </div>
          <span className="analytics-demo__growth-val">{p}%</span>
          <span className="analytics-demo__growth-lab">{labels[i]}</span>
        </div>
      ))}
    </div>
  );
}

export function AnalyticsShowcase() {
  const [tab, setTab] = useState<TabId>('teachers');
  const [selectedTeacher, setSelectedTeacher] = useState(TEACHERS[0].id);
  const [selectedSubject, setSelectedSubject] = useState(SUBJECTS[0].id);
  const [pulse, setPulse] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => setPulse((p) => (p + 1) % 100), 2000);
    return () => window.clearInterval(id);
  }, []);

  const activeTab = TABS.find((t) => t.id === tab) ?? TABS[0];
  const teacher = TEACHERS.find((t) => t.id === selectedTeacher) ?? TEACHERS[0];
  const subject = SUBJECTS.find((s) => s.id === selectedSubject) ?? SUBJECTS[0];

  const ranking = useMemo(
    () => TEACHERS.slice().sort((a, b) => b.avg - a.avg),
    []
  );

  const teachingWell = TEACHERS.filter((t) => t.rating === 'excellent').length;
  const needsLook = TEACHERS.filter((t) => t.rating === 'average' || t.rating === 'needs_support' || t.review).length;

  const overallGrowth = useMemo(() => {
    const first = SUBJECTS.reduce((sum, s) => sum + s.points[0], 0) / SUBJECTS.length;
    const last = SUBJECTS.reduce((sum, s) => sum + s.latest, 0) / SUBJECTS.length;
    return Math.round((last - first) * 10) / 10;
  }, []);

  const latestAverage = Math.round(SUBJECTS.reduce((s, x) => s + x.latest, 0) / SUBJECTS.length);
  const animatedAvg = Math.min(99, Math.max(40, teacher.avg + Math.round(Math.sin(pulse / 14) * 0.4)));
  const teacherRank = ranking.findIndex((r) => r.id === teacher.id) + 1;

  return (
    <div className="analytics-demo">
      <div className="analytics-demo__frame">
        <div className="analytics-demo__chrome" aria-hidden>
          <span className="analytics-demo__chrome-dot" />
          <span className="analytics-demo__chrome-dot" />
          <span className="analytics-demo__chrome-dot" />
          <span className="analytics-demo__chrome-path">skoolfix · insights</span>
          <span className="analytics-demo__chrome-meta">Sample campus · Term II</span>
        </div>

        <div className="analytics-demo__kpis" aria-label="Insights snapshot">
          <div className="analytics-demo__kpi">
            <span className="analytics-demo__kpi-value">{TEACHERS.length}</span>
            <span className="analytics-demo__kpi-label">Classes tracked</span>
          </div>
          <div className="analytics-demo__kpi">
            <span className="analytics-demo__kpi-value">{teachingWell}</span>
            <span className="analytics-demo__kpi-label">Teaching well</span>
          </div>
          <div className="analytics-demo__kpi">
            <span className="analytics-demo__kpi-value">{needsLook}</span>
            <span className="analytics-demo__kpi-label">Worth a look</span>
          </div>
          <div className="analytics-demo__kpi">
            <span className="analytics-demo__kpi-value">+{overallGrowth}</span>
            <span className="analytics-demo__kpi-label">Student growth</span>
          </div>
        </div>

        <div className="analytics-demo__toolbar" role="tablist" aria-label="Analytics demos">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`analytics-demo__tab${tab === t.id ? ' analytics-demo__tab--active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              <span className="analytics-demo__tab-label">{t.label}</span>
              <span className="analytics-demo__tab-hint">{t.hint}</span>
            </button>
          ))}
        </div>

        <p className="analytics-demo__lead">{activeTab.lead}</p>

        <div className="analytics-demo__stage" key={tab}>
          {tab === 'teachers' && (
            <div className="analytics-demo__teachers">
              <aside className="analytics-demo__side" aria-label="Class outcomes">
                <p className="analytics-demo__side-title">Class outcomes</p>
                <p className="analytics-demo__side-hint">Select a class to inspect term score and history.</p>
                <ul className="analytics-demo__list">
                  {TEACHERS.map((row) => {
                    const on = row.id === teacher.id;
                    return (
                      <li key={row.id}>
                        <button
                          type="button"
                          className={`analytics-demo__row${on ? ' analytics-demo__row--on' : ''}`}
                          aria-pressed={on}
                          onClick={() => setSelectedTeacher(row.id)}
                        >
                          <span className="analytics-demo__row-main">
                            <span className="analytics-demo__row-name">{row.teacher}</span>
                            <span className="analytics-demo__row-meta">
                              {row.subject} · {row.klass}
                            </span>
                          </span>
                          <span className="analytics-demo__row-score">{row.avg}%</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </aside>

              <div className="analytics-demo__detail">
                <header className="analytics-demo__detail-head">
                  <div>
                    <p className="analytics-demo__eyebrow">Teaching overview</p>
                    <h3 className="analytics-demo__detail-title">
                      {teacher.teacher}
                      <span>
                        {' '}
                        · {teacher.subject}
                      </span>
                    </h3>
                    <p className="analytics-demo__detail-sub">
                      Class {teacher.klass} · {teacher.students} students · Rank #{teacherRank} on campus
                    </p>
                  </div>
                  <span className={ratingClass(teacher.rating)}>{RATING_LABEL[teacher.rating]}</span>
                </header>

                <div className="analytics-demo__metrics">
                  <div className="analytics-demo__metric" style={{ animationDelay: '0.05s' }}>
                    <span className="analytics-demo__metric-label">Avg. marks</span>
                    <span className="analytics-demo__metric-value">{animatedAvg}%</span>
                  </div>
                  <div className="analytics-demo__metric" style={{ animationDelay: '0.1s' }}>
                    <span className="analytics-demo__metric-label">vs prior term</span>
                    <span
                      className={`analytics-demo__metric-value${
                        teacher.delta >= 0 ? ' is-up' : ' is-down'
                      }`}
                    >
                      {teacher.delta > 0 ? '+' : ''}
                      {teacher.delta}
                    </span>
                  </div>
                  <div
                    className="analytics-demo__metric analytics-demo__metric--spark"
                    style={{ animationDelay: '0.15s' }}
                  >
                    <span className="analytics-demo__metric-label">Growth across terms</span>
                    <Sparkline
                      points={teacher.trend}
                      tone={teacher.delta < 0 ? 'alert' : teacher.review ? 'gold' : 'navy'}
                    />
                  </div>
                </div>

                <div className="analytics-demo__rank" aria-label="Campus ranking preview">
                  <div className="analytics-demo__rank-head">
                    <p className="analytics-demo__side-title">Campus ranking by average</p>
                    <p className="analytics-demo__side-hint">Click any row to switch focus</p>
                  </div>
                  {ranking.map((row, i) => {
                    const active = row.id === teacher.id;
                    return (
                      <button
                        key={row.id}
                        type="button"
                        className={`analytics-demo__bar-row${active ? ' is-active' : ''}`}
                        style={{ animationDelay: `${0.08 + i * 0.05}s` }}
                        onClick={() => setSelectedTeacher(row.id)}
                      >
                        <span className="analytics-demo__bar-rank">{i + 1}</span>
                        <span className="analytics-demo__bar-name">
                          {row.teacher}
                          <em>{row.subject}</em>
                        </span>
                        <div className="analytics-demo__bar-track">
                          <div
                            className="analytics-demo__bar-fill"
                            style={{ width: `${row.avg}%` }}
                          />
                        </div>
                        <span className="analytics-demo__bar-pct">{row.avg}%</span>
                      </button>
                    );
                  })}
                </div>

                <div className="analytics-demo__footer-strip">
                  {teacher.review ? (
                    <p className="analytics-demo__flag analytics-demo__flag--watch">
                      Unusual pattern flagged for admin review — dig into marks on your live campus portal.
                    </p>
                  ) : (
                    <p className="analytics-demo__flag">
                      Sample ratings from published exam averages. Exact thresholds and audits stay in your school
                      portal.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === 'students' && (
            <div className="analytics-demo__students">
              <div className="analytics-demo__student-hero">
                <div>
                  <p className="analytics-demo__eyebrow">AI report analysis</p>
                  <h3 className="analytics-demo__detail-title">Aarav Sharma · Class 10-A</h3>
                  <p className="analytics-demo__growth-headline">
                    Steady climb — overall <strong>+{overallGrowth} pts</strong> since Unit Test 1
                  </p>
                </div>
                <div className="analytics-demo__overall">
                  <span className="analytics-demo__overall-label">Latest average</span>
                  <span className="analytics-demo__overall-value">{latestAverage}%</span>
                  <span className="analytics-demo__overall-delta is-up">+{overallGrowth} pts</span>
                </div>
              </div>

              <div className="analytics-demo__student-grid">
                <aside className="analytics-demo__side" aria-label="Subjects">
                  <p className="analytics-demo__side-title">Subject trends</p>
                  <p className="analytics-demo__side-hint">Open a subject for growth vs class highest.</p>
                  <ul className="analytics-demo__list">
                    {SUBJECTS.map((s) => {
                      const on = s.id === subject.id;
                      return (
                        <li key={s.id}>
                          <button
                            type="button"
                            className={`analytics-demo__row${on ? ' analytics-demo__row--on' : ''}`}
                            aria-pressed={on}
                            onClick={() => setSelectedSubject(s.id)}
                          >
                            <span className="analytics-demo__row-main">
                              <span className="analytics-demo__row-name">{s.name}</span>
                              <span className={statusClass(s.status)}>{statusLabel(s.status)}</span>
                            </span>
                            <span className="analytics-demo__row-score">{s.latest}%</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </aside>

                <div className="analytics-demo__detail">
                  <header className="analytics-demo__detail-head">
                    <div>
                      <p className="analytics-demo__eyebrow">Growth chart</p>
                      <h3 className="analytics-demo__detail-title">{subject.name}</h3>
                      <p className="analytics-demo__detail-sub">
                        Latest {subject.latest}% · Class highest {subject.classHighest}% ·{' '}
                        {subject.classHighest - subject.latest} pts to top
                      </p>
                    </div>
                    <span className={statusClass(subject.status)}>{statusLabel(subject.status)}</span>
                  </header>

                  <GrowthBars points={subject.points} />

                  <div className="analytics-demo__compare">
                    <p className="analytics-demo__compare-title">You vs class highest</p>
                    <div className="analytics-demo__compare-track" aria-hidden>
                      <div
                        className="analytics-demo__compare-you"
                        style={{ width: `${subject.latest}%` }}
                      />
                      <div
                        className="analytics-demo__compare-high"
                        style={{ left: `${subject.classHighest}%` }}
                        title="Class highest"
                      />
                    </div>
                    <div className="analytics-demo__compare-labels">
                      <span>You · {subject.latest}%</span>
                      <span>Class highest · {subject.classHighest}%</span>
                    </div>
                  </div>

                  <ul className="analytics-demo__insights">
                    {STUDENT_INSIGHTS.map((insight, i) => (
                      <li
                        key={insight.title}
                        className={`analytics-demo__insight analytics-demo__insight--${insight.tone}`}
                        style={{ animationDelay: `${0.1 + i * 0.06}s` }}
                      >
                        <strong>{insight.title}</strong>
                        <span>{insight.detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="analytics-demo__footnote">
        Interactive preview — your campus portal uses live marks for teachers, parents, and students.
      </p>
    </div>
  );
}
